import { useCallback, useState } from 'react'
import { confirmStepUp } from '../../../services/biometric-auth-service'

export type StepUpGuardState =
  | { status: 'idle' }
  | { status: 'checking'; reason: string }
  | { status: 'error'; reason: string; message: string }

/**
 * Step-up biometric confirmation for sensitive account actions (password,
 * email, display name changes; account deletion). Call `requireStepUp` right
 * before submitting the sensitive change; render `<StepUpConfirmModal>` bound
 * to the returned `state` so the user sees a "checking\u2026" / retryable-error
 * overlay while the native prompt is in flight.
 *
 * On web, or when the device has no biometrics/passcode configured at all,
 * `confirmStepUp` resolves `{ ok: true, skipped: true }` immediately so this
 * never blocks the action \u2014 see `biometric-auth-service.ts`.
 */
export function useBiometricGuard() {
  const [state, setState] = useState<StepUpGuardState>({ status: 'idle' })

  const attempt = useCallback(async (reason: string): Promise<boolean> => {
    setState({ status: 'checking', reason })
    const result = await confirmStepUp(reason)
    if (result.ok) {
      setState({ status: 'idle' })
      return true
    }
    setState({ status: 'error', reason, message: result.message })
    return false
  }, [])

  const requireStepUp = useCallback((reason: string) => attempt(reason), [attempt])

  const retry = useCallback(() => {
    if (state.status === 'error') void attempt(state.reason)
  }, [state, attempt])

  const dismiss = useCallback(() => setState({ status: 'idle' }), [])

  return { state, requireStepUp, retry, dismiss }
}
